import React, { useCallback, useEffect, useState } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
  withRouter,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { HomeScreen } from '../home';
import PageNotFound from '../pageNotFound/PageNotFound';
import InsuranceNavigation from '../insurance/InsuranceNavigation';
import { FileClaimbotScreen } from '../claims/claimbot/FileClaimbotScreen';
import InboxAndDocsScreen from '../inboxAndDocs/InboxAndDocsScreen';
import { MyAccountNavigation } from '../myAccount/MyAccountNavigation';
import { NavigationMyPets } from '../myPets/NavigationMyPets';
import GenericError from '../genericError/GenericError';
import { FoundLostPet } from '../foundLostPet/FoundLostPet';
import { AcceptTermsOfServiceModal } from '../settings';

import * as routes from './routes.constants';
import Header from './Header';
import './SessionRouter.css';
import { useB2CInfo } from '../../hooks';
import { UrgentMessages } from '../common/UrgentMessages';
import { TermsOfUseScreen } from '../contentfulPages/TermsOfUseScreen';
import { PrivacyPolicyScreen } from '../contentfulPages/PrivacyPolicyScreen';
import { WelcomeScreen } from '../petDemographics';
import { usePetDemographics } from '../petDemographics/hooks';
import { SideAndBurgerMenu } from '../sideMenu/SideAndBurgerMenu';
import { DialogMessage, LogoutModal } from '../common';
import { LANGUAGES_EN_US, LANGUAGES_FR_CA } from '../../i18n';

const SessionRouterApp = () => {
  const store = useSelector(({ common, session }) => ({ common, session }));
  const { isExpired } = store.session;
  const { showGenericError } = store.common;
  const {
    inWelcomeScreen,
    loading: loadingDemographics,
    redirectionRoute,
    successSubmitting,
  } = usePetDemographics();

  const { getAllTokens } = useB2CInfo();
  const history = useHistory();
  const match = useRouteMatch();

  const [contactUsVisible, setContactUsVisible] = useState(false);
  const [modalAddPetVisible, setModalAddPetVisible] = useState(false);

  const toggleModalAddPetVisible = useCallback(() => {
    setModalAddPetVisible((prevShow) => !prevShow);
  }, []);

  useEffect(() => {
    if (showGenericError) {
      history.push(routes.ERROR);
    }

    if (isExpired) {
      getAllTokens();
    }
  }, [showGenericError, isExpired, history, getAllTokens]);

  useEffect(() => {
    const to = sessionStorage.getItem('to');

    if (to) {
      sessionStorage.removeItem('to');

      history.push(to);
    }
  }, []);

  return (
    <Switch>
      <Route exact path={routes.TERMS_OF_USE}>
        <TermsOfUseScreen />
      </Route>

      <Route exact path={routes.PRIVACY_POLICY}>
        <PrivacyPolicyScreen language={LANGUAGES_EN_US} />
      </Route>

      <Route exact path={routes.VIE_PRIVEE}>
        <PrivacyPolicyScreen language={LANGUAGES_FR_CA} />
      </Route>

      <Route path="*">
        <Header
          isBurgerMenuHidden={inWelcomeScreen}
          showLogout={inWelcomeScreen}
        />

        <AcceptTermsOfServiceModal />

        <DialogMessage />

        <LogoutModal />

        <div className="Layout" id="layout">
          {!inWelcomeScreen && (
            <SideAndBurgerMenu
              modalAddPetVisible={modalAddPetVisible}
              toggleModalAddPetVisible={toggleModalAddPetVisible}
            />
          )}

          <div className="Main-content">
            <div className="Main-content">
              <div className="Content">
                <Switch>
                  <Route exact path={routes.WELCOME_DEMOGRAPHICS}>
                    {!loadingDemographics
                      && redirectionRoute !== routes.WELCOME_DEMOGRAPHICS
                      ? <Redirect to={routes.HOME} />
                      : <WelcomeScreen />}
                  </Route>

                  {/* Block all other routes while demographics are
                      pending. Must come before every other route so the
                      Switch evaluates it before matching home or any other
                      path. Skipped on successSubmitting so history.push(HOME)
                      can complete before PdbaData clears in Redux. */}
                  {redirectionRoute === routes.WELCOME_DEMOGRAPHICS
                    && !successSubmitting && (
                    <Redirect to={routes.WELCOME_DEMOGRAPHICS} />
                  )}

                  <Route exact path={['/', routes.HOME]}>
                    <HomeScreen />
                  </Route>

                  <Route path={[routes.B2C_TOKEN_REGISTER]}>
                    <Redirect to={redirectionRoute} />
                  </Route>

                  <Route path={routes.INBOX_DOCS}>
                    <InboxAndDocsScreen />
                  </Route>

                  <Route path={routes.MY_ACCOUNT}>
                    <MyAccountNavigation />
                  </Route>

                  <Route exact path={routes.MY_PETS}>
                    <Redirect to="/insurance" />
                  </Route>

                  <Route path={[`${routes.MY_PETS}/:id`, routes.MY_PETS]}>
                    <NavigationMyPets
                      contactUsVisible={contactUsVisible}
                      setContactUsVisible={setContactUsVisible}
                    />
                  </Route>

                  <Route path={[
                    routes.ORCA_CHANGE_COVERAGE,
                    routes.ORCA_CHANGE_COVERAGE_OPTIONS,
                    routes.INSURANCE,
                    routes.CHANGE_COVERAGE,
                    routes.CLAIMS,
                    `${match.path}/details`,
                  ]}
                  >
                    <InsuranceNavigation />
                  </Route>

                  <Route path={routes.FILE_CLAIMBOT}>
                    <FileClaimbotScreen />
                  </Route>

                  {/* TODO uncomment when is ready to prod
                <Route path="/vets">
                  {comingSoon}
                </Route> */}

                  <Route path={routes.ERROR}>
                    <GenericError />
                  </Route>

                  <Route exact path={routes.FOUND_LOST_PET}>
                    <FoundLostPet />
                  </Route>

                  <Route path={routes.DEMOGRAPHICS}>
                    {/* Once the user finishes up the certificate
                    linking flow, then is redirected to  home */}
                    <Redirect to={routes.LANDING_ROUTE} />
                  </Route>

                  <Route path="*">
                    <PageNotFound />
                  </Route>
                </Switch>
              </div>
            </div>
          </div>
        </div>

        <UrgentMessages />
      </Route>
    </Switch>
  );
};

export default withRouter(SessionRouterApp);
